import {
    Grid,
    PargodaRoundButton,
    useAppDispatch,
    useAppSelector,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
    Icon,
} from '../../base';
import {
    PageWrapper,
    HeaderWrapper,
    ContentWrapper,
    FooterWrapper,
} from './JobApplicationEntity.styles';
import PositionAndNavigation from './PositionAndNavigation';
import StepsTemplate from './StepsTemplate';
import {
    JobApplicationStatuses,
    JobApplicationStatusUpdateValues,
} from '../../common';
import CustomStepper from './Stepper';
import {
    IUpdateJobApplicationStatusAction,
    jobApplicationActions,
    jobApplicationSelectors,
    jobsActions,
} from '../../actions';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import CandidatesMenuDrawer from './CandidatesMenuDrawer';
import ApplicationFooter from './ApplicationFooter';

interface Props {
    applicationId: string;
}

const JobApplicationEntity = (props: Props) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const theme = useTheme();
    const { applicationId } = props;
    const activeJobApplication = useAppSelector(
        jobApplicationSelectors.getActiveJobApplication
    );
    // Ignore stale Redux data from the previous application while the new one loads.
    // Without this, swapping routes keeps showing the prior app (incl. interview questions).
    const jobApplicationData =
        activeJobApplication &&
        String(activeJobApplication.id) === String(applicationId)
            ? activeJobApplication
            : null;
    const isUpdateStatusLoading = useAppSelector(
        jobApplicationSelectors.getIsUpdateJobApplicationStatusLoading
    );

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    useEffect(() => {
        dispatch(
            jobApplicationActions.setActiveJobApplication({
                jobApplication: null,
            })
        );
        dispatch(
            jobApplicationActions.fetchJobActiveApplication({
                id: applicationId,
            })
        );
    }, [applicationId, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(
                jobApplicationActions.setActiveJobApplication({
                    jobApplication: null,
                })
            );
            dispatch(jobsActions.setActiveJobCandidates({ candidates: [] }));
            dispatch(jobsActions.setActiveCandidateJobId({ jobId: null }));
        };
    }, [dispatch]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const isRejectClicked = () => {
        const payload: IUpdateJobApplicationStatusAction = {
            stepId: jobApplicationData?.currentStep || 0,
            status: JobApplicationStatusUpdateValues.reject,
        };
        dispatch(jobApplicationActions.setRejectApplicationModalData(payload));
        dispatch(jobApplicationActions.setIsRejectApplicationModalOpen(true));
    };

    const isProceedClicked = () => {
        const payload: IUpdateJobApplicationStatusAction = {
            status: JobApplicationStatusUpdateValues.accept,
            stepId: jobApplicationData?.currentStep || 0,
        };
        dispatch(jobApplicationActions.updateJobApplicationStatus(payload));
    };

    const handleMoveBack = () => {
        handleMenuClose();
        if (!jobApplicationData?.steps) return;

        const currentStepIndex = jobApplicationData.steps.findIndex(
            (step) => step.id === jobApplicationData.currentStep
        );
        if (currentStepIndex <= 0) return;

        const _previousStep = jobApplicationData.steps[currentStepIndex - 1];

        // Reset current step status to null
        const resetCurrentStepPayload: IUpdateJobApplicationStatusAction = {
            status: JobApplicationStatusUpdateValues.none,
            stepId: jobApplicationData.currentStep || 0,
        };
        dispatch(
            jobApplicationActions.updateJobApplicationStatus(
                resetCurrentStepPayload
            )
        );
    };

    const handleUndoRejectOrWithdraw = () => {
        handleMenuClose();
        const payload: IUpdateJobApplicationStatusAction = {
            status: JobApplicationStatusUpdateValues.inProcess,
            stepId: jobApplicationData?.currentStep || 0,
            applicationStatus: JobApplicationStatuses.inReview,
        };
        dispatch(jobApplicationActions.updateJobApplicationStatus(payload));
    };

    const isLegacy = jobApplicationData?.isLegacy ?? false;

    const isActionButtonsDisabled =
        isLegacy ||
        isUpdateStatusLoading ||
        (jobApplicationData?.status &&
            ![
                JobApplicationStatuses.new,
                JobApplicationStatuses.inReview,
            ].includes(jobApplicationData.status));

    // Get current step data
    const currentStepData = jobApplicationData?.steps?.find(
        (step) => step.id === jobApplicationData.currentStep
    );
    const isCurrentStepRejected =
        currentStepData?.status === JobApplicationStatusUpdateValues.reject;
    const isCurrentStepWithdrawn =
        currentStepData?.status === JobApplicationStatusUpdateValues.withdraw;

    // Check if we can move back (current step index > 0 AND not rejected/withdrawn)
    const currentStepIndex =
        jobApplicationData?.steps?.findIndex(
            (step) => step.id === jobApplicationData?.currentStep
        ) ?? -1;
    const canMoveBack =
        currentStepIndex > 0 &&
        !isCurrentStepRejected &&
        !isCurrentStepWithdrawn;

    // Check if menu should be enabled (can move back OR can undo reject/withdraw)
    const isMenuEnabled =
        canMoveBack || isCurrentStepRejected || isCurrentStepWithdrawn;

    return (
        <>
            <PageWrapper>
                {jobApplicationData && (
                    <>
                        <HeaderWrapper>
                            <Grid container rowSpacing={2} direction="row">
                                <Grid item xs={3}>
                                    <PositionAndNavigation
                                        jobData={jobApplicationData.job}
                                    />
                                </Grid>
                                <Grid item xs={5} sx={{ paddingInlineEnd: 2 }}>
                                    <CustomStepper
                                        steps={jobApplicationData.steps}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        wrap="nowrap"
                                        spacing={1}
                                    >
                                        <Grid item sx={{ flex: 1 }}>
                                            <PargodaRoundButton
                                                textColor={
                                                    theme.palette.primary.main
                                                }
                                                variant="outlined"
                                                loadingPosition="start"
                                                onClick={isRejectClicked}
                                                startIcon={<></>}
                                                loading={isUpdateStatusLoading}
                                                disabled={
                                                    isActionButtonsDisabled
                                                }
                                                fullWidth
                                            >
                                                {t('jobApplicationPage.reject')}
                                            </PargodaRoundButton>
                                        </Grid>
                                        <Grid item sx={{ flex: 1 }}>
                                            <PargodaRoundButton
                                                variant="contained"
                                                loadingPosition="start"
                                                onClick={isProceedClicked}
                                                startIcon={<></>}
                                                loading={isUpdateStatusLoading}
                                                disabled={
                                                    isActionButtonsDisabled
                                                }
                                                fullWidth
                                            >
                                                {t(
                                                    'jobApplicationPage.proceed'
                                                )}
                                            </PargodaRoundButton>
                                        </Grid>
                                        {(isCurrentStepRejected ||
                                            isCurrentStepWithdrawn ||
                                            currentStepIndex > 0) && (
                                            <Grid item>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleMenuOpen}
                                                    disabled={
                                                        isLegacy ||
                                                        !isMenuEnabled ||
                                                        isUpdateStatusLoading
                                                    }
                                                    sx={{
                                                        padding:
                                                            theme.spacing(0.25),
                                                        height: '22px',
                                                        width: '22px',
                                                    }}
                                                >
                                                    <Icon
                                                        name="MoreVertIcon"
                                                        size={18}
                                                    />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={menuAnchorEl}
                                                    open={isMenuOpen}
                                                    onClose={handleMenuClose}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal:
                                                            theme.direction ===
                                                            'rtl'
                                                                ? 'left'
                                                                : 'right',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal:
                                                            theme.direction ===
                                                            'rtl'
                                                                ? 'left'
                                                                : 'right',
                                                    }}
                                                >
                                                    {canMoveBack && (
                                                        <MenuItem
                                                            onClick={
                                                                handleMoveBack
                                                            }
                                                        >
                                                            <Icon
                                                                name="RotateLeftOutlinedIcon"
                                                                size={16}
                                                            />
                                                            <span
                                                                style={{
                                                                    marginInlineStart: 8,
                                                                }}
                                                            >
                                                                {t(
                                                                    'jobApplicationPage.moveBack'
                                                                )}
                                                            </span>
                                                        </MenuItem>
                                                    )}
                                                    {isCurrentStepRejected && (
                                                        <MenuItem
                                                            onClick={
                                                                handleUndoRejectOrWithdraw
                                                            }
                                                        >
                                                            <Icon
                                                                name="RotateLeftOutlinedIcon"
                                                                size={16}
                                                            />
                                                            <span
                                                                style={{
                                                                    marginLeft: 8,
                                                                }}
                                                            >
                                                                {t(
                                                                    'jobApplicationPage.undoReject'
                                                                )}
                                                            </span>
                                                        </MenuItem>
                                                    )}
                                                    {isCurrentStepWithdrawn && (
                                                        <MenuItem
                                                            onClick={
                                                                handleUndoRejectOrWithdraw
                                                            }
                                                        >
                                                            <Icon
                                                                name="RotateLeftOutlinedIcon"
                                                                size={16}
                                                            />
                                                            <span
                                                                style={{
                                                                    marginLeft: 8,
                                                                }}
                                                            >
                                                                {t(
                                                                    'jobApplicationPage.undoWithdraw'
                                                                )}
                                                            </span>
                                                        </MenuItem>
                                                    )}
                                                </Menu>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </HeaderWrapper>
                        <ContentWrapper>
                            <StepsTemplate
                                jobApplicationData={jobApplicationData}
                            />
                        </ContentWrapper>
                        <FooterWrapper>
                            <ApplicationFooter />
                        </FooterWrapper>
                    </>
                )}
            </PageWrapper>
            {jobApplicationData && <CandidatesMenuDrawer />}
        </>
    );
};

export default JobApplicationEntity;
